import React, { useState, useEffect, useRef } from "react";

import crossIcon from "../../../assets/wrong-icon.png";
import checkIcon from "../../../assets/check-icon.png";
import { useNavigate } from "react-router-dom";
import { getQuizesByQuizeId } from "../../../services/quiz/getQuizeByQuizeId";
import {
	startQuizAttempt,
	submitQuizAnswer,
	completeQuizAttempt,
} from "../../../services/quiz/quizAttempt";
import MusicQuizCard from "./components/MusicQuizCard";
import "./styles/QuizPage.css";

// You can replace this with an imported SVG if you have one
const CrossIcon = () => (
	<img
		src={crossIcon}
		alt='Cross Icon'
		style={{
			width: 22,
			height: 22,
			display: "flex",
			alignItems: "center",
		}}
	/>
);
const CheckIcon = () => (
	<img
		src={checkIcon}
		alt='Check Icon'
		style={{
			width: 22,
			height: 22,
			display: "flex",
			alignItems: "center",
		}}
	/>
);

export default function QuizPage() {
	const scrollContainerRef = useRef(null);
	// Use empty arrays initially, will set correct length after data load
	const [selected, setSelected] = useState([]);
	const [showAnswer, setShowAnswer] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [quizes, setQuizes] = useState(null);
	const [quizAttemptId, setQuizAttemptId] = useState(null);
	const [answerStartTimes, setAnswerStartTimes] = useState({});
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [allQuestionsAnswered, setAllQuestionsAnswered] = useState(false);
	const [answerStatuses, setAnswerStatuses] = useState({});
	const [quizError, setQuizError] = useState("");
	const [submitError, setSubmitError] = useState("");
	const answerRecordsRef = useRef({});
	const completingRef = useRef(false);

	const navigate = useNavigate();

	const searchParams = new URLSearchParams(window.location.search);
	const quizzesPerPage = 5;

	const quizeId = searchParams.get("quizeId");

	const debugQuiz = (...details) => {
		if (process.env.NODE_ENV !== "production")
			console.debug("Quiz flow:", ...details);
	};

	const fetchQuizesById = async () => {
		try {
			const response = await getQuizesByQuizeId(quizeId);
			if (!Array.isArray(response)) {
				setQuizError(
					"Unable to load quiz questions. Please try again later.",
				);
				return;
			}

			debugQuiz(
				"quizId",
				quizeId,
				"questionIds",
				response.map((q) => q.id),
			);
			const mismatched = response.filter((q) => {
				const ownerId = q.quizId ?? q.quiz_id ?? q.quizID ?? q.quiz?.id;
				return ownerId != null && String(ownerId) !== String(quizeId);
			});
			if (mismatched.length > 0) {
				console.error("Quiz question ownership mismatch", {
					quizId: quizeId,
					questionIds: mismatched.map((q) => q.id),
				});
				setQuizError(
					"This quiz returned questions from another quiz. Please contact support.",
				);
				return;
			}

			if (response) {
				// Transform API response to match UI's expected structure
				const transformed = response.map((q) => {
					const options =
						q.questionOptions?.map((opt) => opt.optionText) || [];
					const correctIndex = q.questionOptions?.findIndex(
						(opt) => opt.isCorrect,
					);
					return {
						id: q.id,
						question: q.questionText,
						options,
						correctIndex,
						questionText: q.questionText,
						questionType: q.questionType,
						correctAnswer:
							q.correctAnswer ||
							q.questionOptions?.[correctIndex]?.optionText ||
							"",
						pianoFromNote: q.pianoFromNote || null,
						pianoToNote: q.pianoToNote || null,
						highlightedKey: q.highlightedKey || null,
						imgURL: q.imgURL || null,
						questionOptions: q.questionOptions || [],
					};
				});
				answerRecordsRef.current = {};
				setAnswerStatuses({});
				setAllQuestionsAnswered(false);
				setSubmitError("");
				setQuizError("");
				setQuizAttemptId(null);
				setCurrentPage(1);
				setQuizes(transformed);
				setSelected(Array(transformed.length).fill(null));
				setShowAnswer(Array(transformed.length).fill(false));

				// Initialize answer start times for all questions
				const startTimes = {};
				transformed.forEach((q) => {
					startTimes[q.id] = Date.now();
				});
				setAnswerStartTimes(startTimes);

				// Start the quiz attempt
				if (transformed.length > 0) startQuizAttemptHandler(quizeId);
			}
		} catch (error) {
			console.error("Error fetching quizzes:", error);
			setQuizError(
				"Unable to load quiz questions. Please try again later.",
			);
		}
	};

	// Start quiz attempt
	const startQuizAttemptHandler = async (quizId) => {
		try {
			// Identity now comes solely from the JWT on the server side - no client-supplied id.
			const response = await startQuizAttempt(quizId);
			if (response?.id) {
				debugQuiz("startedAttemptId", response.id);
				setQuizAttemptId(response.id);
			} else {
				setQuizError(
					"Unable to start this quiz. Please try again later.",
				);
			}
		} catch (error) {
			console.error("Error starting quiz attempt:", error);
			setQuizError("Unable to start this quiz. Please try again later.");
		}
	};

	useEffect(() => {
		if (quizeId) {
			fetchQuizesById();
		}
		// eslint-disable-next-line
	}, [quizeId]);

	useEffect(() => {
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		}
	}, [currentPage]);

	// Display the fetched questions in pages
	const quizData = quizes || [];
	const totalPages = Math.ceil(quizData.length / quizzesPerPage);
	const startIdx = (currentPage - 1) * quizzesPerPage;
	const endIdx = startIdx + quizzesPerPage;
	const quizzesToShow = quizData.slice(startIdx, endIdx);

	const updateAnswerStatus = (questionId, status) => {
		answerRecordsRef.current[questionId].status = status;
		setAnswerStatuses((previous) => ({
			...previous,
			[questionId]: status,
		}));
		setAllQuestionsAnswered(
			quizes.length > 0 &&
				quizes.every(
					(q) => answerRecordsRef.current[q.id]?.status === "saved",
				),
		);
	};

	const saveAnswer = async (questionId, payload) => {
		answerRecordsRef.current[questionId] = { payload, status: "pending" };
		updateAnswerStatus(questionId, "pending");
		setSubmitError("");
		debugQuiz("answerQuestionId", questionId);
		try {
			await submitQuizAnswer(
				quizAttemptId,
				questionId,
				payload.userAnswer,
				payload.answerTimeSeconds,
			);
			updateAnswerStatus(questionId, "saved");
		} catch (error) {
			console.error("Unable to save quiz answer:", {
				questionId,
				status: error.response?.status,
				message: error.message,
			});
			updateAnswerStatus(questionId, "failed");
			setSubmitError(
				"An answer was not saved. Retry it before submitting the quiz.",
			);
		}
	};

	const handleSelect = (quizIdx, answer, options = {}) => {
		if (!quizAttemptId || !quizes?.[quizIdx]) return;
		const quiz = quizes[quizIdx];
		const questionId = quiz.id;
		const existing = answerRecordsRef.current[questionId];
		if (existing?.status === "pending" || existing?.status === "saved")
			return;

		const userAnswer =
			options.directAnswer === true || quiz.questionType === "TEXT_ANSWER"
				? String(answer).trim()
				: (quiz.options?.[answer] ?? String(answer));
		if (!userAnswer) return;

		const newSelected = [...selected];
		newSelected[quizIdx] = answer;
		setSelected(newSelected);
		const newShow = [...showAnswer];
		newShow[quizIdx] = true;
		setShowAnswer(newShow);

		const startTime = answerStartTimes[questionId] || Date.now();
		const answerTimeSeconds =
			options.answerTimeSeconds ??
			Math.round((Date.now() - startTime) / 1000);
		saveAnswer(questionId, { userAnswer, answerTimeSeconds });
	};

	const retryAnswer = (questionId) => {
		const record = answerRecordsRef.current[questionId];
		if (record?.status === "failed" && quizAttemptId)
			saveAnswer(questionId, record.payload);
	};

	const handleSubmitQuiz = async () => {
		if (!quizAttemptId || completingRef.current) return;
		const ready =
			quizes?.length > 0 &&
			quizes.every(
				(q) => answerRecordsRef.current[q.id]?.status === "saved",
			);
		if (!ready) {
			setSubmitError(
				"Answer and save every question before submitting the quiz.",
			);
			return;
		}

		completingRef.current = true;
		setIsSubmitting(true);
		setSubmitError("");
		debugQuiz("completionAttemptId", quizAttemptId);
		try {
			const result = await completeQuizAttempt(quizAttemptId);
			if (result) {
				navigate("/home/quiz-result", {
					state: { quizResult: result },
				});
			} else {
				setSubmitError(
					"Could not complete the quiz. Please try again.",
				);
			}
		} catch (error) {
			console.error("Error completing quiz:", error);
			setSubmitError("Could not complete the quiz. Please try again.");
		} finally {
			completingRef.current = false;
			setIsSubmitting(false);
		}
	};

	const pendingAnswerCount = Object.values(answerStatuses).filter(
		(status) => status === "pending",
	).length;

	const handleNextPage = () => {
		setCurrentPage(currentPage + 1);
		if (scrollContainerRef.current) {
			scrollContainerRef.current.scrollTo({
				top: 0,
				behavior: "smooth",
			});
		}
	};

	return (
		<div
			ref={scrollContainerRef}
			style={{
				height: "73%",
				overflowY: "scroll",
				overflowX: "hidden",
				width: "98%",
				display: "flex",
				alignItems: "flex-start",
			}}>
			<div
				style={{
					background: "#E0EDFE",
					borderRadius: 32,
					width: "100%",
					padding: "40px 40px 0",
					boxShadow: "0 2px 16px #0001",
					marginLeft: 40,
					marginTop: 10,
					display: "flex",
					flexDirection: "column",
					justifyContent: "space-between",
				}}>
				<div style={{ flex: 1 }}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							gap: 60,
						}}>
						{quizError ? (
							<p role='alert' style={{ color: "#b3261e" }}>
								{quizError}
							</p>
						) : !quizes ? (
							<p>Loading quiz questions...</p>
						) : quizes.length === 0 ? (
							<p>No quiz questions are available.</p>
						) : !quizAttemptId ? (
							<p>Starting quiz attempt...</p>
						) : (
							quizzesToShow.map((quiz, idx) => (
								<React.Fragment key={quiz.id}>
									<QuizCard
										quiz={quiz}
										idx={startIdx + idx}
										selected={selected[startIdx + idx]}
										showAnswer={showAnswer[startIdx + idx]}
										onSelect={handleSelect}
									/>
									{answerStatuses[quiz.id] === "pending" && (
										<p role='status'>Saving answer...</p>
									)}
									{answerStatuses[quiz.id] === "saved" && (
										<p
											role='status'
											style={{ color: "#1ca15f" }}>
											Answer saved.
										</p>
									)}
									{answerStatuses[quiz.id] === "failed" && (
										<div
											role='alert'
											style={{ color: "#b3261e" }}>
											Answer not saved.{" "}
											<button
												type='button'
												onClick={() =>
													retryAnswer(quiz.id)
												}>
												Retry saving
											</button>
										</div>
									)}
									{idx < quizzesToShow.length - 1 && (
										<div
											style={{
												borderBottom: "2px solid #bbb",
												margin: "-30px 0",
											}}
										/>
									)}
								</React.Fragment>
							))
						)}
					</div>

					{/* Submit Quiz Button - Only shown on the last page when all questions are answered */}
					{currentPage === totalPages &&
						quizAttemptId &&
						!quizError && (
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									gap: 8,
									marginTop: 40,
									marginBottom: 40,
								}}>
								<button
									onClick={handleSubmitQuiz}
									disabled={
										isSubmitting ||
										pendingAnswerCount > 0 ||
										!allQuestionsAnswered
									}
									style={{
										padding: "12px 32px",
										borderRadius: 24,
										background: "#1976d2",
										color: "#fff",
										fontWeight: 600,
										fontSize: 16,
										border: "none",
										cursor:
											isSubmitting ||
											pendingAnswerCount > 0 ||
											!allQuestionsAnswered
												? "not-allowed"
												: "pointer",
										opacity:
											isSubmitting ||
											pendingAnswerCount > 0 ||
											!allQuestionsAnswered
												? 0.7
												: 1,
										boxShadow:
											"0 2px 8px rgba(25, 118, 210, 0.3)",
									}}>
									{isSubmitting
										? "Submitting..."
										: "Submit Quiz"}
								</button>
								{pendingAnswerCount > 0 && (
									<p role='status'>
										Waiting for answers to save...
									</p>
								)}
								{!allQuestionsAnswered &&
									pendingAnswerCount === 0 && (
										<p role='status'>
											Answer and save every question to
											submit the quiz.
										</p>
									)}
								{submitError && (
									<p
										role='alert'
										style={{ color: "#b3261e" }}>
										{submitError}
									</p>
								)}
							</div>
						)}
				</div>
				{totalPages > 1 && (
					<div
						className='pagination-controls'
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 8,
							marginBottom: 40,
							marginTop: 20,
							background: "#e6f0fd",
							borderRadius: 24,
							padding: "16px 24px",
							width: "fit-content",
							marginLeft: "auto",
							marginRight: "auto",
						}}>
						{/* Left Arrow */}
						<button
							style={{
								background:
									currentPage === 1 ? "#cfe2f7" : "#1976d2",
								border: "none",
								borderRadius: "50%",
								width: 36,
								height: 36,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color: currentPage === 1 ? "#a0b8d9" : "#fff",
								fontSize: 20,
								cursor:
									currentPage === 1
										? "not-allowed"
										: "pointer",
								marginRight: 8,
								transition: "background 0.2s",
							}}
							disabled={currentPage === 1}
							onClick={() => {
								setCurrentPage(currentPage - 1);
								if (scrollContainerRef.current) {
									scrollContainerRef.current.scrollTo({
										top: 0,
										behavior: "smooth",
									});
								}
							}}>
							{"\u25C0"}
						</button>

						{/* Page Numbers */}
						{[...Array(totalPages)].map((_, i) => (
							<button
								key={i}
								style={{
									background:
										currentPage === i + 1
											? "#0a1a4f"
											: "#fff",
									color:
										currentPage === i + 1
											? "#fff"
											: "#0a1a4f",
									border: "none",
									borderRadius: "50%",
									width: 32,
									height: 32,
									fontWeight: 600,
									margin: "0 4px",
									fontSize: 16,
									boxShadow: "0 1px 4px #0001",
									cursor: "pointer",
									transition: "background 0.2s, color 0.2s",
								}}
								onClick={() => {
									setCurrentPage(i + 1);
									if (scrollContainerRef.current) {
										scrollContainerRef.current.scrollTo({
											top: 0,
											behavior: "smooth",
										});
									}
								}}>
								{i + 1}
							</button>
						))}

						{/* Right Arrow */}
						<button
							style={{
								background:
									currentPage === totalPages
										? "#cfe2f7"
										: "#1976d2",
								border: "none",
								borderRadius: "50%",
								width: 36,
								height: 36,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								color:
									currentPage === totalPages
										? "#a0b8d9"
										: "#fff",
								fontSize: 20,
								cursor:
									currentPage === totalPages
										? "not-allowed"
										: "pointer",
								marginLeft: 8,
								transition: "background 0.2s",
							}}
							disabled={currentPage === totalPages}
							onClick={() => {
								handleNextPage();
							}}>
							{"\u25B6"}
						</button>
					</div>
				)}
			</div>
		</div>
	);
}

function QuizCard({ quiz, idx, selected, showAnswer, onSelect }) {
	// Determine if the answer is correct or wrong
	const isCorrect = selected === quiz.correctIndex;

	// For TEXT_ANSWER type
	const [textAnswer, setTextAnswer] = React.useState("");
	const [textAnswered, setTextAnswered] = React.useState(false);
	const [textCorrect, setTextCorrect] = React.useState(false);

	const handleTextSubmit = () => {
		const answer = textAnswer.trim();
		if (!answer) return;
		setTextAnswered(true);
		setTextCorrect(
			answer.toLowerCase() === quiz.correctAnswer.trim().toLowerCase(),
		);
		onSelect(idx, answer);
	};

	// Handle music quiz question types
	const isMusicQuiz = [
		"TEXT_NOTE_TO_PIANO",
		"PIANO_TO_TEXT_NOTE",
		"SYMBOL_NOTE_TO_PIANO",
		"PIANO_TO_SYMBOL_NOTE",
		"IMAGE_CHOICE",
	].includes(quiz.questionType);

	if (isMusicQuiz) {
		return (
			<MusicQuizCard
				quiz={quiz}
				idx={idx}
				onCorrectAnswer={(
					idx,
					userAnswer,
					answerTimeSeconds,
					isCorrect,
				) => {
					// For music quizzes, we pass the isCorrect parameter directly from the MusicQuizCard
					onSelect(idx, userAnswer, {
						directAnswer: true,
						answerTimeSeconds,
					});
				}}
			/>
		);
	}

	if (quiz.questionType === "TEXT_ANSWER") {
		return (
			<div style={{ marginBottom: 8 }}>
				<div
					style={{
						fontFamily: "Inter, sans-serif",
						fontWeight: 500,
						fontSize: 20,
						lineHeight: "100%",
						letterSpacing: 0,
						marginBottom: 5,
						color: "#000000",
						textAlign: "left",
					}}>
					{idx + 1}. {quiz.question}
				</div>
				{/* Piano UI area left empty as requested */}
				{/* <div style={{ height: 120, margin: '24px 0' }}>
          
        </div> */}
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
					}}>
					{/* <div style={{ fontWeight: 500, fontSize: 18, marginBottom: 0 }}>Type the name of the note into the box</div> */}
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							alignItems: "center",
							marginTop: 8,
						}}>
						<input
							type='text'
							value={textAnswer}
							onChange={(e) => setTextAnswer(e.target.value)}
							disabled={textAnswered}
							placeholder='Type answer here'
							style={{
								width: 220,
								height: 36,
								border: "1px solid #ccc",
								borderRadius: 18,
								fontSize: 16,
								padding: "0 12px",
								marginRight: 8,
								outline: "none",
								background: textAnswered ? "#f5f5f5" : "#fff",
							}}
						/>
						{!textAnswered && (
							<button
								onClick={handleTextSubmit}
								disabled={!textAnswer.trim()}
								style={{
									height: 36,
									borderRadius: 18,
									background: "#1976d2",
									color: "#fff",
									fontWeight: 600,
									fontSize: 14,
									border: "none",
									padding: "0 18px",
									cursor: "pointer",
									marginLeft: 0,
								}}>
								Submit
							</button>
						)}
					</div>
				</div>
				{textAnswered && (
					<div
						style={{
							color: textCorrect ? "#1ca15f" : "#d32f2f",
							fontWeight: 600,
							fontSize: 15,
							marginTop: 16,
							textAlign: "left",
						}}>
						{textCorrect ? (
							<>Correct Answer</>
						) : (
							<>
								Wrong Answer. Correct Answer:{" "}
								{quiz.correctAnswer}
							</>
						)}
					</div>
				)}
			</div>
		);
	}

	if (quiz.questionType === "TRUE_FALSE") {
		// For TRUE_FALSE questions, create two buttons for True and False
		const options = ["True", "False"];
		const correctIndex =
			quiz.correctAnswer.toLowerCase() === "true" ? 0 : 1;

		return (
			<div style={{ marginBottom: 20, minHeight: 10 }}>
				<div
					style={{
						fontFamily: "Inter, sans-serif",
						fontWeight: 500,
						fontSize: 20,
						lineHeight: "100%",
						letterSpacing: 0,
						marginBottom: 5,
						color: "#000000",
						textAlign: "left",
					}}>
					{idx + 1}. {quiz.question}
				</div>
				<div
					style={{
						display: "flex",
						gap: 30,
						flexWrap: "wrap",
						marginBottom: 40,
					}}>
					{options.map((opt, optIdx) => {
						let border = "none";
						let boxShadow = "0 1px 4px #0001";
						let icon = null;
						if (showAnswer && selected === optIdx) {
							if (optIdx === correctIndex) {
								border = "2px solid #1ca15f";
								boxShadow = "0 0 0 2px #1ca15f33";
								icon = <CheckIcon />;
							} else {
								border = "2px solid #d32f2f";
								boxShadow = "0 0 0 2px #d32f2f33";
								icon = <CrossIcon />;
							}
						}
						return (
							<button
								key={opt}
								onClick={() => onSelect(idx, optIdx)}
								disabled={showAnswer}
								style={{
									width: 350,
									height: 40,
									padding: 18,
									borderRadius: 24,
									border,
									background: "#fff",
									color: "#222",
									fontWeight: 500,
									fontSize: 20,
									marginBottom: -40,
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									cursor: showAnswer ? "default" : "pointer",
									outline: "none",
									boxShadow,
									position: "relative",
									transition: "border 0.2s",
								}}>
								<span style={{ flex: 1, textAlign: "left" }}>
									{String.fromCharCode(65 + optIdx)}. {opt}
								</span>
								{icon && (
									<div
										style={{
											marginLeft: 8,
											flexShrink: 0,
										}}>
										{icon}
									</div>
								)}
							</button>
						);
					})}
				</div>
				{showAnswer && (
					<div
						style={{
							color:
								selected === correctIndex
									? "#1ca15f"
									: "#d32f2f",
							fontWeight: 600,
							fontSize: 15,
							marginTop: 50,
							marginBottom: 20,
							textAlign: "left",
						}}>
						{selected === correctIndex ? (
							<>
								Correct Answer :{" "}
								{String.fromCharCode(65 + correctIndex)}
							</>
						) : (
							<>
								Wrong Answer. Correct Answer :{" "}
								{String.fromCharCode(65 + correctIndex)}
							</>
						)}
					</div>
				)}
			</div>
		);
	}

	return (
		<div style={{ marginBottom: 8, minHeight: 10 }}>
			<div
				style={{
					fontFamily: "Inter, sans-serif",
					fontWeight: 500,
					fontSize: 20,
					lineHeight: "100%",
					letterSpacing: 0,
					marginBottom: 5,
					color: "#000000",
					textAlign: "left",
				}}>
				{idx + 1}. {quiz.question}
			</div>
			<div
				style={{
					display: "flex",
					gap: 30,
					flexWrap: "wrap",
					marginBottom: 40,
				}}>
				{quiz.options.map((opt, optIdx) => {
					let border = "none";
					let boxShadow = "0 1px 4px #0001";
					let icon = null;
					if (showAnswer && selected === optIdx) {
						if (optIdx === quiz.correctIndex) {
							border = "2px solid #1ca15f";
							boxShadow = "0 0 0 2px #1ca15f33";
							icon = <CheckIcon />;
						} else {
							border = "2px solid #d32f2f";
							boxShadow = "0 0 0 2px #d32f2f33";
							icon = <CrossIcon />;
						}
					}
					return (
						<button
							key={opt}
							onClick={() => onSelect(idx, optIdx)}
							disabled={showAnswer}
							style={{
								width: 350,
								height: 40,
								padding: 18,
								borderRadius: 24,
								border,
								background: "#fff",
								color: "#222",
								fontWeight: 500,
								fontSize: 20,
								marginBottom: -40,
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								cursor: showAnswer ? "default" : "pointer",
								outline: "none",
								boxShadow,
								position: "relative",
								transition: "border 0.2s",
							}}>
							<span style={{ flex: 1, textAlign: "left" }}>
								{String.fromCharCode(65 + optIdx)}. {opt}
							</span>
							{icon && (
								<div style={{ marginLeft: 8, flexShrink: 0 }}>
									{icon}
								</div>
							)}
						</button>
					);
				})}
			</div>
			{showAnswer && (
				<div
					style={{
						color: isCorrect ? "#1ca15f" : "#d32f2f",
						fontWeight: 600,
						fontSize: 15,
						marginTop: 80,
						marginBottom: 20,
						textAlign: "left",
					}}>
					{isCorrect ? (
						<>
							Correct Answer :{" "}
							{String.fromCharCode(65 + quiz.correctIndex)}
						</>
					) : (
						<>
							Wrong Answer. Correct Answer :{" "}
							{String.fromCharCode(65 + quiz.correctIndex)}
						</>
					)}
				</div>
			)}
		</div>
	);
}
